import { useRef, useState, type ChangeEvent, type DragEvent } from 'react';
import { Button, cn, documentFiles, filesFromDataTransfer } from '@dataroom/shared';

interface UploadZoneProps {
  onFiles: (files: File[]) => void;
  disabled?: boolean;
}

/**
 * Gated multi-file drop zone. Drag-and-drop of one or many files (or a folder,
 * walked recursively with OS clutter skipped — same helper as the seller's) is
 * the progressive enhancement; the always-present, keyboard-focusable buttons —
 * "Browse files" (multi-select) and "Upload a folder" — are the accessible path.
 */
export function UploadZone({ onFiles, disabled = false }: UploadZoneProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const folderRef = useRef<HTMLInputElement | null>(null);
  const [dragging, setDragging] = useState(false);

  async function handleDrop(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setDragging(false);
    if (disabled) return;
    const files = await filesFromDataTransfer(e.dataTransfer);
    if (files.length > 0) onFiles(files);
  }

  function handlePick(e: ChangeEvent<HTMLInputElement>) {
    const files = documentFiles(e.target.files ?? []);
    if (files.length > 0) onFiles(files);
    e.target.value = ''; // allow re-selecting the same file(s)
  }

  return (
    <div
      onDragOver={(e) => {
        e.preventDefault();
        if (!disabled) setDragging(true);
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={(e) => void handleDrop(e)}
      className={cn(
        'flex flex-col items-center justify-center gap-3 rounded-lg border-2 border-dashed p-8 text-center transition-colors',
        dragging ? 'border-primary bg-primary/5' : 'border-input',
        disabled && 'pointer-events-none opacity-60',
      )}
    >
      <p className="text-sm text-muted-foreground">Drag &amp; drop files or a folder here, or</p>
      <div className="flex flex-wrap items-center justify-center gap-2">
        <Button
          type="button"
          variant="outline"
          disabled={disabled}
          onClick={() => inputRef.current?.click()}
        >
          Browse files
        </Button>
        <Button
          type="button"
          variant="ghost"
          disabled={disabled}
          onClick={() => folderRef.current?.click()}
        >
          Upload a folder
        </Button>
      </div>
      <input
        ref={inputRef}
        type="file"
        multiple
        className="sr-only"
        aria-label="Choose files to upload to this room"
        onChange={handlePick}
      />
      {/* Folder picker. `webkitdirectory`/`directory` have no typed React prop, so
          they are set on the element through a callback ref. */}
      <input
        ref={(el) => {
          folderRef.current = el;
          if (el) {
            el.setAttribute('webkitdirectory', '');
            el.setAttribute('directory', '');
          }
        }}
        type="file"
        multiple
        className="sr-only"
        aria-label="Choose a folder to upload to this room"
        onChange={handlePick}
      />
      <p className="text-xs text-muted-foreground">
        Any file type, any batch size — each file is queued for automatic categorization. Max 50 MB
        per file.
      </p>
    </div>
  );
}
